/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AppEventBusService} from '../../events/app-event-bus.service';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BsDatepickerConfig, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {PopoverDirective} from 'ngx-bootstrap/popover';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';

import template from './chart-options.component.html';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_STAT_TYPES, ADN_CHART_Y_AXIS_OPTIONS} from '../../options/chart-options';
import {getAvailableFields} from './chart-options-fields';
import {AngularModalService} from '../../modal/angular-modal.service';
import {ChartOptionsFieldsModalComponent} from './chart-options-fields-modal.component';
import {applyNetworkCalendarWeekStart, toBsDateInputFormat} from '../form-controls/bs-datepicker-locale';
import {apiInstantFromPickerDate, pickerDateFromApiInstant} from '../../locale/network-date-picker-utils';
import {AppLocaleService} from '../../locale/app-locale.service';
import {getIntlLocaleData, AngularLocaleData} from '../../locale/intl-locale-data';
import {UserProfileApiService} from '../../../admin/user-profile/user-profile-api.service';
import {AdnTimeShifterService} from '../../../../components/util/adn-time-shifter.service';
import {ChartOptionsUtil} from '../../options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../../angular-platform-providers';
import {CHART_IMPORTS} from '../chart-imports';

const DATE_POPOVER_INSIDE_SELECTOR = [
  '.adn-chart-options-date-popover',
  'bs-datepicker-container',
  '.bs-datepicker'
].join(', ');

@Component({
  imports: CHART_IMPORTS,
  selector: 'adn-chart-options',
  template
})
export class ChartOptionsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() adnDateOptionId = '';
  @Input() adnStartDate: any;
  @Input() adnEndDate: any;
  @Input() adnCountTypeId = '';
  @Input() adnStatTypeId = '';
  @Input() adnShowExpandedDownload: any;
  @Input() adnChartType = '';
  @Input() adnSelFieldIds: any;
  @Input() adnSelFieldId: any;
  @Input() adnYAxisOptionId = '';
  @Input() adnIsCollection: any;
  @Input() adnCurrency = '';
  @Input() adnSelectedCurrency: any;
  @Input() adnMinimalCollection: any;
  @Input() adnDateSelectorId = '';
  @Input() adnOptions = '';

  @Output() adnChangeCallback = new EventEmitter<any>();
  @Output() adnSaveCallback = new EventEmitter<any>();

  countTypes = _.values(ADN_CHART_COUNT_TYPES);
  statTypes = _.values(ADN_CHART_STAT_TYPES);
  yAxisOptions = _.values(ADN_CHART_Y_AXIS_OPTIONS);
  countTypesMap = ADN_CHART_COUNT_TYPES;
  statTypesMap = ADN_CHART_STAT_TYPES;
  yAxisOptionsMap = ADN_CHART_Y_AXIS_OPTIONS;

  sel: any = {};
  showAudit = {val: false};
  manualDateChange = false;
  availableCurrencies: any[] = [];
  dateOptions: any = {};
  dateOptionsFiltered: any[] = [];
  dateOptionsLeft: any[] = [];
  dateOptionsRight: any[] = [];
  dates: any = {};
  startTime: Date;
  endTime: Date;
  datesError = '';
  fields: any[] = [];
  datePlaceholder: string;
  bsConfig: Partial<BsDatepickerConfig>;

  countTypeDelay = false;
  statTypeDelay = false;
  axisDelay = false;
  fieldsDelay = false;
  singFieldsDelay = false;
  datesDelay = false;

  resolvedOptions = 'LINE_CHART_OPTIONS';
  isCollection = false;
  isMinimalCollection = false;
  showExpandedDownload = false;
  resolvedChartType = '';

  @ViewChild('datePopoverTrigger', {read: ElementRef}) datePopoverTrigger: ElementRef<HTMLElement>;
  @ViewChild('datePopoverTrigger', {read: PopoverDirective}) datePopoverDir: PopoverDirective;

  private resetFieldsMethod: () => void = _.noop;
  private suppressDateChangeCallbacks = false;
  private suppressDateChangeTimer: any;
  private chartDatesEventTimer: any;
  private datePopoverOutsideUnlisten: (() => void) | null = null;
  private readonly localeData: AngularLocaleData;

  constructor(
    private readonly eventBus: AppEventBusService,
    private readonly appLocale: AppLocaleService,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly angularModalService: AngularModalService,
    private readonly destroyRef: DestroyRef,
    private readonly bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService,
    private readonly renderer: Renderer2
  ) {
    this.localeData = getIntlLocaleData(this.appLocale.getLocaleId());
    this.datePlaceholder = this.localeData.DATETIME_FORMATS.shortDate.replace(/yy/g, 'y');
    this.bsConfig = this.createBsConfig();
  }

  ngOnInit(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );
  }

  ngOnDestroy(): void {
    if (this.chartDatesEventTimer) {
      clearTimeout(this.chartDatesEventTimer);
    }
    this.unbindDatePopoverOutsideClick();
  }

  ngOnChanges(changes: SimpleChanges): void {
    void changes;
    this.rebuildState();
  }

  selectCountType(countType: any): void {
    this.sel.countType = countType;
    this.updateChart('countType');
  }

  selectStatType(statType: any): void {
    this.sel.statType = statType;
    this.updateChart('statType');
  }

  selectYAxisOption(yAxisOption: any): void {
    this.sel.yAxisOption = yAxisOption;
    this.updateChart('axis');
  }

  selectDateOption(option: any): void {
    this.sel.dateOption = option;
    this.updateDates(true);
    this.datePopoverDir?.hide();
  }

  selectCurrency(code: string): void {
    this.sel.currency = code;
    this.updateChart('currency', false, true);
  }

  selectSingularField(fieldId: string): void {
    this.sel.field = fieldId;
    this.updateChart('singFields');
  }

  get chartRangeStartInstant(): Date | null {
    return this.dates.startDate
      ? apiInstantFromPickerDate(this.dates.startDate, this.adnTimeShifter)
      : null;
  }

  get chartRangeEndInstant(): Date | null {
    return this.dates.endDate
      ? apiInstantFromPickerDate(this.dates.endDate, this.adnTimeShifter)
      : null;
  }

  isFieldSelected(field: any): boolean {
    return _.some(this.sel.fields, {id: field.id});
  }

  toggleField(field: any, event: Event): void {
    const checked = !!(event.target as HTMLInputElement).checked;
    if (checked) {
      this.sel.fields = (this.sel.fields || []).concat([field]);
    } else {
      this.sel.fields = _.filter(this.sel.fields, (f: any) => f.id !== field.id);
    }
    this.updateChart('fields');
  }

  onAuditChange(): void {
    this.updateChart('audit');
  }

  dateChangeCallback(resetTimes?: boolean): void {
    if (this.suppressDateChangeCallbacks) {
      return;
    }
    this.manualDateChange = true;
    if (resetTimes) {
      const dates = this.adnTimeShifter.startAndEndOfDays(this.dates.startDate, this.dates.endDate);
      this.setDisplayDates(dates.startDate, dates.endDate);
    }
  }

  onDateFieldChange(field: 'startDate' | 'endDate', value: Date): void {
    if (this.suppressDateChangeCallbacks) {
      return;
    }
    this.dates[field] = value;
    this.dateChangeCallback(true);
  }

  onTimeFieldChange(field: 'startDate' | 'endDate', value: Date): void {
    if (this.suppressDateChangeCallbacks) {
      return;
    }
    this.dates[field] = new Date(value);
    this.dateChangeCallback();
  }

  updateDates(selectedDateOption?: any): void {
    this.datesError = '';
    if (selectedDateOption) {
      this.manualDateChange = false;
    } else {
      if (moment(this.dates.startDate).isAfter(moment(this.dates.endDate))) {
        this.datesError = 'The end date must be after the start date.';
        return;
      }
      this.sel.dateOption = null;
      this.manualDateChange = false;
    }
    this.setDates(selectedDateOption);
    this.updateChart('dates', true);
  }

  applyCustomRange(): void {
    this.updateDates();
    this.datePopoverDir?.hide();
  }

  onDatePopoverShown(): void {
    this.bindDatePopoverOutsideClick();
  }

  onDatePopoverHidden(): void {
    this.unbindDatePopoverOutsideClick();
  }

  selectFields(): void {
    const chartOptions = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    if (!chartOptions) {
      return;
    }
    const profile = this.localUserProfile.get().profile;
    const profileParam = chartOptions.chartTotalsOptions;
    const availableFields = _.get(profile, [profileParam, 'availableFields'], []);
    const fields = _.values(chartOptions.fields);

    // Click inside the fields popover already scheduled a zoneless tick
    // (setTimeout). A plain setTimeout(0) often runs first, so ngx-bootstrap
    // creates this modal mid-check (NG0100: undefined → 'button.cancel').
    void this.angularModalService.showAfterTick(ChartOptionsFieldsModalComponent, {
      class: 'modal-md',
      initialState: {
        fields,
        availableFieldIds: availableFields,
        chartType: this.resolvedChartType,
        profileParam,
        onSaved: () => this.updateChart('availableFields')
      }
    }, this.destroyRef);
  }

  updateChart(prefix: string, isDateChange?: boolean, isCurrencyChange?: boolean): void {
    if (prefix === 'availableFields') {
      this.resetFieldsMethod();
    }
    this[prefix + 'Delay'] = true;
    setTimeout(() => {
      this[prefix + 'Delay'] = false;
    }, 21);

    setTimeout(() => {
      const theDates = this.sendRootScope(isDateChange, isCurrencyChange);
      this.adnChangeCallback.emit({
        dateOptionId: theDates.dateOptionId,
        startDate: theDates.startDate,
        endDate: theDates.endDate,
        countTypeId: this.sel.countType.id,
        statTypeId: this.sel.statType.id,
        yAxisOptionId: this.sel.yAxisOption.id,
        selFieldIds: _.map(this.sel.fields, 'id'),
        selFieldId: this.sel.field,
        selCurrency: this.sel.currency,
        showAudit: this.showAudit.val
      });
    }, 101);
  }

  saveXls(allFields?: boolean, totalsOnly?: boolean): void {
    this.adnSaveCallback.emit({allFields, format: 'xlsx', totalsOnly});
  }

  private rebuildState(): void {
    this.resolvedChartType = this.adnChartType;
    this.resolvedOptions = this.adnOptions || 'LINE_CHART_OPTIONS';
    this.isCollection = this.coerceBoolean(this.adnIsCollection);
    this.isMinimalCollection = this.adnMinimalCollection === true;
    this.showExpandedDownload = !!this.adnShowExpandedDownload;

    this.sel = {};
    this.showAudit = {val: false};
    this.manualDateChange = false;
    this.availableCurrencies = [];
    this.dates = {};
    this.datesError = '';
    this.fields = [];

    this.dateOptions = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType);
    // Queries (PIE_CHART_OPTIONS) are multi-object — "Use Line Item Dates" does not apply.
    if (this.resolvedOptions === 'PIE_CHART_OPTIONS') {
      delete this.dateOptions.useDates;
    }
    this.dateOptionsFiltered = _.filter(_.values(this.dateOptions), (option: any) => option && !option.undated);
    const half = Math.floor(this.dateOptionsFiltered.length / 2);
    this.dateOptionsLeft = this.dateOptionsFiltered.slice(0, half);
    this.dateOptionsRight = this.dateOptionsFiltered.slice(half);

    const preferredCurrency = this.localUserProfile.getPreferredCurrency();
    const defaultCurrency = this.LocalNetworkProfile.get('defaultCurrency');
    if (this.adnCurrency) {
      const chartType = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
      const currencyType = chartType && chartType.type === 'PUBLISHING' ? 'Floor Price' : 'Bid';
      this.availableCurrencies.push({code: this.adnCurrency, text: this.adnCurrency + ' - ' + currencyType + ' Currency'});
    }
    if (preferredCurrency) {
      this.availableCurrencies.push({code: preferredCurrency, text: preferredCurrency + ' - Preferred Currency'});
    }
    this.availableCurrencies.push({code: defaultCurrency, text: defaultCurrency + ' - Network Currency'});
    this.availableCurrencies = _.uniqBy(this.availableCurrencies, 'code');
    if (this.availableCurrencies.length === 1) {
      this.availableCurrencies = [];
    }
    this.sel.currency = this.adnSelectedCurrency || this.adnCurrency || preferredCurrency || defaultCurrency;

    this.resetFieldsMethod = _.noop;
    if (this.resolvedOptions === 'LINE_CHART_OPTIONS' && this.resolvedChartType) {
      this.resetFieldsMethod = () => {
        const selectableFields = getAvailableFields(this.ChartOptionsUtil, this.localUserProfile, this.resolvedChartType, this.location);
        this.fields = _.filter(selectableFields, (field: any) => {
          return !field.availability
            || (this.LocalNetworkProfile.isPureMarketplacePlatform() && field.availability.MARKETPLACE)
            || (!this.LocalNetworkProfile.isPureMarketplacePlatform() && field.availability.DIRECT);
        });
        const selFieldIds = _.isArray(this.adnSelFieldIds) ? this.adnSelFieldIds : [];
        this.sel.fields = _.filter(this.fields, (value: any) => selFieldIds.indexOf(value.id) > -1);
        this.sel.field = this.adnSelFieldId;
      };
      this.resetFieldsMethod();
    }

    if (this.adnDateOptionId) {
      this.sel.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[this.adnDateOptionId];
    }
    if (!this.sel.dateOption && (!this.adnStartDate || !this.adnEndDate)) {
      this.sel.dateOption = this.dateOptions.last12Hours;
    }

    this.sel.yAxisOption = this.adnYAxisOptionId ? this.yAxisOptionsMap[this.adnYAxisOptionId] : this.yAxisOptionsMap.multiple;
    this.sel.countType = this.adnCountTypeId ? this.countTypesMap[this.adnCountTypeId] : this.countTypesMap.singular;
    this.sel.statType = this.adnStatTypeId ? this.statTypesMap[this.adnStatTypeId] : this.statTypesMap.impression;
    this.setDates(this.sel.dateOption);
    this.sendRootScope(true);
  }

  private setDates(selectedDateOption?: any): void {
    this.dates = this.dates || {};
    if (selectedDateOption) {
      if (this.sel.dateOption.undated) {
        this.setDisplayDates(undefined, undefined);
        return;
      }
      this.setDisplayDates(
        pickerDateFromApiInstant(this.sel.dateOption.startDate().toDate(), this.adnTimeShifter),
        pickerDateFromApiInstant(this.sel.dateOption.endDate().toDate(), this.adnTimeShifter)
      );
      return;
    }
    if (!this.dates.startDate || !this.dates.endDate) {
      if (_.get(this, ['sel', 'dateOption'])) {
        this.setDisplayDates(
          pickerDateFromApiInstant(this.sel.dateOption.startDate().toDate(), this.adnTimeShifter),
          pickerDateFromApiInstant(this.sel.dateOption.endDate().toDate(), this.adnTimeShifter)
        );
      } else {
        this.setDisplayDates(
          pickerDateFromApiInstant(moment.utc(this.adnStartDate).toDate(), this.adnTimeShifter),
          pickerDateFromApiInstant(moment.utc(this.adnEndDate).toDate(), this.adnTimeShifter)
        );
      }
    }
  }

  private sendRootScope(isDateChange?: boolean, isCurrencyChange?: boolean): any {
    const dateOptionId = _.get(this.sel.dateOption, 'id');
    let correctDates: any = {startDate: undefined, endDate: undefined, dateOptionId};
    if (dateOptionId !== 'useDates') {
      const startDate = dateOptionId
        ? apiInstantFromPickerDate(this.dates.startDate, this.adnTimeShifter)
        : moment(this.dates.startDate);
      const endDate = dateOptionId
        ? apiInstantFromPickerDate(this.dates.endDate, this.adnTimeShifter)
        : moment(this.dates.endDate);
      const startDateString = moment(startDate).format('YYYY-MM-DD HH:mm');
      const endDateString = moment(endDate).format('YYYY-MM-DD HH:mm');
      const timelessTimezonedStartDate = moment.tz(startDateString, this.ChartOptionsUtil.getNetworkTimezone());
      const timelessTimezonedEndDate = moment.tz(endDateString, this.ChartOptionsUtil.getNetworkTimezone());

      correctDates = {
        startDate: dateOptionId ? startDate : timelessTimezonedStartDate,
        endDate: dateOptionId ? endDate : timelessTimezonedEndDate,
        dateOptionId,
        currency: this.sel.currency
      };
    }
    const chartType = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    if ((isCurrencyChange || isDateChange) && chartType) {
      const eventName = 'chartDates' + chartType.idKey + (this.adnDateSelectorId || '');
      this.ChartOptionsUtil.setLastChartDates(eventName, correctDates);
      // Input changes can rebuild this component before a queued date event
      // fires. Only the newest selection should reach sibling widgets.
      if (this.chartDatesEventTimer) {
        clearTimeout(this.chartDatesEventTimer);
      }
      this.chartDatesEventTimer = setTimeout(() => {
        this.chartDatesEventTimer = null;
        this.eventBus.emit(eventName, correctDates);
      }, 101);
    }
    return correctDates;
  }

  private setDisplayDates(startDate: any, endDate: any): void {
    this.suppressDateChangeCallbacks = true;
    if (this.suppressDateChangeTimer) {
      clearTimeout(this.suppressDateChangeTimer);
    }
    this.dates.startDate = startDate;
    this.dates.endDate = endDate;
    this.startTime = startDate ? new Date(startDate) : undefined;
    this.endTime = endDate ? new Date(endDate) : undefined;
    this.suppressDateChangeTimer = setTimeout(() => {
      this.suppressDateChangeCallbacks = false;
      this.suppressDateChangeTimer = null;
    });
  }

  private coerceBoolean(value: any): boolean {
    return value === '' || value === true || value === 'true';
  }

  private bindDatePopoverOutsideClick(): void {
    this.unbindDatePopoverOutsideClick();
    // Skip the click that opened the popover.
    setTimeout(() => {
      this.datePopoverOutsideUnlisten = this.renderer.listen('document', 'click', (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) {
          return;
        }
        // closest() still walks detached calendar nodes after a day is chosen.
        if (target.closest(DATE_POPOVER_INSIDE_SELECTOR)) {
          return;
        }
        if (this.datePopoverTrigger?.nativeElement?.contains(target)) {
          return;
        }
        this.datePopoverDir?.hide();
      });
    });
  }

  private unbindDatePopoverOutsideClick(): void {
    if (this.datePopoverOutsideUnlisten) {
      this.datePopoverOutsideUnlisten();
      this.datePopoverOutsideUnlisten = null;
    }
  }

  private createBsConfig(): Partial<BsDatepickerConfig> {
    return {
      dateInputFormat: toBsDateInputFormat(this.localeData.DATETIME_FORMATS.shortDate),
      adaptivePosition: false,
      returnFocusToInput: false,
      selectFromOtherMonth: true,
      showTodayButton: true,
      showClearButton: true,
      todayPosition: 'left',
      clearPosition: 'right',
      todayButtonLabel: this.translate.instant('button.today'),
      clearButtonLabel: this.translate.instant('button.clear')
    };
  }
}
